import { createContext, useContext, useState, useEffect } from "react";
import apiClient from "../services/api";
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Use sessionStorage instead of localStorage - clears when browser closes
  const [token, setToken] = useState(sessionStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [loginPassword, setLoginPassword] = useState(null); // Store for password change

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await apiClient.get(`/auth/me`);
          setUser(response.data);
          setMustChangePassword(response.data.must_change_password || false);
        } catch (error) {
          console.error("Auth init error:", error);
          sessionStorage.removeItem("token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [token]);

  // Re-validate session when tab becomes visible after being idle
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible" && token) {
        try {
          await apiClient.get(`/auth/me`, {
            timeout: 5000,
          });
        } catch (error) {
          if (error.response?.status === 401) {
            sessionStorage.removeItem("token");
            setToken(null);
            setUser(null);
            window.location.href = "/login";
          }
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [token]);

  const login = async (username, password) => {
    const response = await apiClient.post(`/auth/login`, {
      username,
      password,
    });
    const { token: newToken, user: userData } = response.data;
    sessionStorage.setItem("token", newToken);
    sessionStorage.setItem("just_logged_in", "true"); // Flag for passkey prompt
    setToken(newToken);
    setUser(userData);
    
    // Check if password change is required
    if (userData.must_change_password) {
      setMustChangePassword(true);
      setLoginPassword(password); // Store current password for change
    }
    
    return userData;
  };

  const logout = () => {
    sessionStorage.removeItem("token");
    setToken(null);
    setUser(null);
    setMustChangePassword(false);
    setLoginPassword(null);
  };

  const passwordChanged = () => {
    setMustChangePassword(false);
    setLoginPassword(null);
    // Update user state
    setUser(prev => prev ? { ...prev, must_change_password: false } : null);
  };

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${token}`,
  });

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        logout,
        getAuthHeaders,
        mustChangePassword,
        loginPassword,
        passwordChanged,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
